/**
 * sl-qndg.5.3 / sl-mkx.6: Template Prerequisites Hook
 *
 * Fetches the pre-install surface (credentials + config refs + column
 * preview + trigger fields + nested-workflow refs) from the backend so
 * the ImportSetupWizard can render a preview before collecting bindings.
 *
 * DIP: the frontend consumes the SSOT that the concretizer uses rather
 * than re-parsing templateData client-side.
 *
 * sl-mkx.6-rework: the backend endpoint is workspace-scoped
 * (`/api/workspace/:workspaceId/marketplace/templates/:templateId/prerequisites`)
 * so the WorkspaceMembershipGuard can authorize every read. The hook
 * must therefore receive `workspaceId` alongside `templateId`; callers
 * that cannot supply both pass `null` and the fetch is skipped.
 */
import { useCallback, useEffect, useState } from 'react';

import { createMarketplaceResponseError } from './marketplace-error';
import { useMarketplaceInstallStateRefresh } from './marketplace-install-state';

interface InFlightPrerequisitesRequest {
  promise: Promise<TemplatePrerequisites>;
  controller: AbortController;
  subscribers: number;
  abortTimer: ReturnType<typeof setTimeout> | null;
}

const inFlightPrerequisitesRequests = new Map<
  string,
  InFlightPrerequisitesRequest
>();

function prerequisitesRequestKey(input: {
  workspaceId: string;
  templateId: string;
  targetVersionNumber?: number;
}): string {
  return [
    input.workspaceId,
    input.templateId,
    input.targetVersionNumber ?? 'latest',
  ].join('\u0000');
}

function prerequisitesUrl(input: {
  workspaceId: string;
  templateId: string;
  targetVersionNumber?: number;
}): string {
  const url = new URL(
    `/api/workspace/${input.workspaceId}/marketplace/templates/${input.templateId}/prerequisites`,
    window.location.origin
  );
  if (input.targetVersionNumber != null) {
    url.searchParams.set(
      'targetVersionNumber',
      String(input.targetVersionNumber)
    );
  }
  return url.pathname + url.search;
}

function acquirePrerequisitesRequest(
  key: string,
  url: string
): InFlightPrerequisitesRequest {
  const existing = inFlightPrerequisitesRequests.get(key);
  if (existing) {
    if (existing.abortTimer) {
      clearTimeout(existing.abortTimer);
      existing.abortTimer = null;
    }
    existing.subscribers += 1;
    return existing;
  }

  const controller = new AbortController();
  const request: InFlightPrerequisitesRequest = {
    controller,
    subscribers: 1,
    abortTimer: null,
    promise: fetch(url, {
      credentials: 'include',
      signal: controller.signal,
    }).then(async res => {
      if (!res.ok) {
        throw await createMarketplaceResponseError(
          res,
          `Failed to load prerequisites: ${res.status}`
        );
      }
      return res.json() as Promise<TemplatePrerequisites>;
    }),
  };
  inFlightPrerequisitesRequests.set(key, request);
  const clearRequest = () => {
    if (inFlightPrerequisitesRequests.get(key) === request) {
      inFlightPrerequisitesRequests.delete(key);
    }
  };
  void request.promise.then(clearRequest, clearRequest);
  return request;
}

function releasePrerequisitesRequest(
  key: string,
  request: InFlightPrerequisitesRequest
): void {
  request.subscribers = Math.max(0, request.subscribers - 1);
  if (
    request.subscribers > 0 ||
    inFlightPrerequisitesRequests.get(key) !== request
  ) {
    return;
  }

  request.abortTimer = setTimeout(() => {
    if (
      request.subscribers === 0 &&
      inFlightPrerequisitesRequests.get(key) === request
    ) {
      request.controller.abort();
      inFlightPrerequisitesRequests.delete(key);
    }
  }, 0);
}

function isAbortError(error: unknown): boolean {
  return (
    !!error &&
    typeof error === 'object' &&
    'name' in error &&
    error.name === 'AbortError'
  );
}

export interface CredentialPrerequisite {
  /** Credential id as it appears in `{{credential.<id>.<field>}}` refs. */
  id: string;
  /** Credential-type slug (matches workspaceCredential.service). */
  type: string;
  label: string;
  required: boolean;
  isProvidedByStreamlain: boolean;
  description?: string;
}

export interface ConfigPrerequisite {
  key: string;
  required: boolean;
  defaultValue?: string;
}

export interface ConfigPreviewItem {
  id: string;
  stepName?: string;
  key: string;
  label?: string;
  labelsI18n?: Record<string, string>;
  value: string;
}

/** sl-mkx.5.3: install-time trigger parameter the installer must supply. */
export interface ParameterizedTriggerFieldView {
  fieldRef: string;
  kind: 'cron' | 'webhook_url' | 'db_column';
  label: string;
  default?: string;
  required: boolean;
}

/**
 * sl-qndg.8.1 column preview entry. PII column names are redacted by
 * the server-side redactor (preview-redactor.ts) before they reach the
 * client — install still ingests the original names.
 */
export interface ColumnPreview {
  id: string;
  name: string;
  type: string;
  redacted: boolean;
  databaseKey?: string;
  databaseLabel?: string;
  databaseRole?: 'input' | 'output' | 'database';
  databaseIndex?: number;
}

/**
 * sl-67kc.4.3 nested-workflow reference emitted by the backend
 * prerequisites scanner. Today the step-type catalog ships no
 * workflow-invocation primitive, so this array is empty for every
 * real template. Surface-only in the wizard — the install-time guard
 * in template-install.service.ts is the authoritative enforcer.
 */
export interface NestedWorkflowPrerequisite {
  workflowId: string;
  referencedAtStepId?: string;
}

export interface TemplateDependencyInfo {
  templateId: string;
  templateType: string;
  title: string;
  versionNumber: number;
  canonicalKey: string;
  majorVersion: number;
  semanticVersion: string;
  contractHash: string;
  status: 'installed' | 'preinstalled' | 'missing' | 'version_conflict';
  installedVersionNumber?: number | null;
  installedSemanticVersion?: string | null;
  requiredByTemplateId: string;
  stepInsertType?: string | null;
}

export interface TemplateDependencyPlan {
  templateId: string;
  dependencies: TemplateDependencyInfo[];
  missingTemplateIds: string[];
  confirmationRequired: boolean;
  permissions?: TemplateInstallPermissionPlan;
}

export type TemplateInstallPermissionReason =
  | 'create_docs'
  | 'create_automation'
  | 'install_workflow'
  | 'install_steps';

export interface TemplateInstallPermissionRequirement {
  permission: string;
  reason: TemplateInstallPermissionReason;
}

export interface TemplateInstallPermissionPlan {
  allowed: boolean;
  required: TemplateInstallPermissionRequirement[];
  missing: TemplateInstallPermissionRequirement[];
}

export interface SlotPreview {
  name: string;
  type: string;
  required: boolean;
  description?: string;
  labelsI18n?: Record<string, string>;
  descriptionsI18n?: Record<string, string>;
}

export interface SlotsPreview {
  inputs: SlotPreview[];
  outputs: SlotPreview[];
}

export interface TemplatePrerequisites {
  credentials: CredentialPrerequisite[];
  config: ConfigPrerequisite[];
  configPreview: ConfigPreviewItem[];
  agentContext?: AgentContextPrerequisite;
  parameterizedTriggerFields?: ParameterizedTriggerFieldView[];
  columnPreview: ColumnPreview[];
  nestedWorkflows: NestedWorkflowPrerequisite[];
  slotsPreview?: SlotsPreview;
  dependencies?: TemplateDependencyPlan;
  permissions?: TemplateInstallPermissionPlan;
  compositePreflight?: {
    automationCount: number;
    maxAutomations: 8;
    revisions: Array<{
      slotKey: string;
      label: string;
      disposition: 'reused' | 'new' | 'derived';
      inputs: string[];
      outputs: string[];
    }>;
    credentialRequirements: string[];
    endpoints: Array<{
      url: string;
      classification: 'public' | 'parameterized' | 'internal';
    }>;
    sharedResources: string[];
    findings: Array<{
      severity: 'info' | 'warning' | 'error';
      message: string;
    }>;
    canSaveTechnicalRevision: boolean;
    canPromoteListing: boolean;
  };
}

export interface AgentContextPrerequisite {
  agentName: string;
  grantsSupported: boolean;
  sources: {
    personalMemory?: AgentContextCapability;
    knowledgeGraph?: AgentContextCapability;
    documents?: AgentContextCapability;
    tools?: AgentContextCapability;
  };
  costDisclosure: {
    usesAiCredits: boolean;
    message: string;
  };
}

export interface AgentContextCapability {
  label: string;
  source: 'workspace_ai_settings' | 'install_grants' | 'workspace_role';
  configurable: boolean;
  disabledReason?: string;
}

export function useTemplatePrerequisites(
  workspaceId: string | null | undefined,
  templateId: string | null,
  targetVersionNumber?: number
) {
  const [prerequisites, setPrerequisites] =
    useState<TemplatePrerequisites | null>(null);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [errorCode, setErrorCode] = useState<string | null>(null);
  const [refreshRevision, setRefreshRevision] = useState(0);
  const refetch = useCallback(() => {
    setRefreshRevision(value => value + 1);
  }, []);
  useMarketplaceInstallStateRefresh(workspaceId, refetch);

  useEffect(() => {
    if (!templateId || !workspaceId) {
      setPrerequisites(null);
      setError(null);
      setErrorCode(null);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setError(null);
    setErrorCode(null);
    setPrerequisites(null);

    const key = prerequisitesRequestKey({
      workspaceId,
      templateId,
      targetVersionNumber,
    });
    const request = acquirePrerequisitesRequest(
      key,
      prerequisitesUrl({ workspaceId, templateId, targetVersionNumber })
    );

    request.promise
      .then(data => {
        if (!cancelled) setPrerequisites(data);
      })
      .catch((err: unknown) => {
        if (!cancelled && !isAbortError(err)) {
          const error = err instanceof Error ? err : new Error(String(err));
          setPrerequisites(null);
          setError(error.message);
          setErrorCode((error as Error & { code?: string }).code ?? null);
        }
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });

    return () => {
      cancelled = true;
      releasePrerequisitesRequest(key, request);
    };
  }, [workspaceId, templateId, targetVersionNumber, refreshRevision]);

  return { prerequisites, isLoading, error, errorCode, refetch };
}
